import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Zap, Search, RefreshCw, AlertTriangle, CheckCircle2,
  Loader2, ChevronRight, Eye, Check,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { storeApi, formatApiError } from '../lib/api';
import { CATEGORY_LABELS, PRODUCT_STATUS_CONFIG, formatPrice } from '../components/store/storeConstants';

// Operazioni massive disponibili (specchio del backend)
const OPERATIONS = [
  { type: 'set_base_price', label: 'Imposta prezzo base', group: 'Prezzi' },
  { type: 'set_promo_price', label: 'Imposta prezzo promo', group: 'Prezzi' },
  { type: 'set_customer_price', label: 'Imposta prezzo cliente', group: 'Prezzi' },
  { type: 'discount_percent', label: 'Applica sconto percentuale', group: 'Prezzi' },
  { type: 'discount_amount', label: 'Applica sconto importo', group: 'Prezzi' },
  { type: 'set_vat', label: 'Modifica IVA', group: 'Prezzi' },
  { type: 'set_promo_validity', label: 'Imposta validità promo', group: 'Promo' },
  { type: 'link_promo', label: 'Collega promo esistente', group: 'Promo' },
  { type: 'remove_promo', label: 'Rimuovi promo', group: 'Promo' },
  { type: 'set_voucher', label: 'Voucher SI/NO', group: 'Promo' },
  { type: 'set_tasso_zero', label: 'Tasso zero SI/NO', group: 'Promo' },
  { type: 'set_fido_bolletta', label: 'FIDO in bolletta SI/NO', group: 'Promo' },
  { type: 'set_status', label: 'Cambia stato (attiva/disattiva/archivia)', group: 'Stato' },
  { type: 'set_visibility', label: 'Visibilità agli agenti', group: 'Stato' },
  { type: 'set_commercial_notes', label: 'Aggiorna note commerciali', group: 'Note' },
];

const PRICE_TARGETS = [
  { value: 'promo_price', label: 'Prezzo promo' },
  { value: 'base_price', label: 'Prezzo base' },
  { value: 'customer_price', label: 'Prezzo cliente' },
];

const StepBadge = ({ number, label, active, done }) => (
  <div className={`flex items-center gap-2 ${active ? '' : 'opacity-50'}`}>
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
      done ? 'bg-green-500 text-white' : active ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
    }`}>
      {done ? <Check className="h-4 w-4" /> : number}
    </div>
    <span className={`text-sm font-medium ${active ? 'text-slate-900' : 'text-muted-foreground'}`}>{label}</span>
  </div>
);

export default function StoreBulkEditPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1: selezione
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({ search: '', category: '', brand: '', product_line: '', status: '', promo_code: '' });
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Step 2: operazione
  const [operation, setOperation] = useState('');
  const [params, setParams] = useState({});

  // Step 3: anteprima
  const [preview, setPreview] = useState(null);
  const [working, setWorking] = useState(false);

  const loadMeta = useCallback(async () => {
    try {
      const response = await storeApi.getProductsMeta();
      setMeta(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (filters.search) params.search = filters.search;
      if (filters.category) params.category = filters.category;
      if (filters.brand) params.brand = filters.brand;
      if (filters.product_line) params.product_line = filters.product_line;
      if (filters.status) params.status = filters.status;
      const response = await storeApi.getProducts(params);
      let items = response.data.items || [];
      if (filters.promo_code) items = items.filter((p) => p.promo_code === filters.promo_code);
      setProducts(items);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadProducts(); }, [loadProducts]);

  const allVisible = products.length > 0 && products.every((p) => selectedIds.has(p.id));

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisible) products.forEach((p) => next.delete(p.id));
      else products.forEach((p) => next.add(p.id));
      return next;
    });
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handlePreview = async () => {
    setWorking(true);
    try {
      const response = await storeApi.bulkPreview({
        product_ids: Array.from(selectedIds),
        operation: { type: operation, params },
      });
      setPreview(response.data);
      setStep(3);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setWorking(false);
    }
  };

  const handleApply = async () => {
    setWorking(true);
    try {
      const response = await storeApi.bulkApply({
        product_ids: Array.from(selectedIds),
        operation: { type: operation, params },
      });
      toast.success(`${response.data.message} (batch ${response.data.batch_id})`);
      // Reset completo
      setStep(1);
      setSelectedIds(new Set());
      setOperation('');
      setParams({});
      setPreview(null);
      loadProducts();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setWorking(false);
    }
  };

  const setParam = (key) => (value) => setParams((p) => ({ ...p, [key]: value }));

  const paramsValid = useMemo(() => {
    switch (operation) {
      case 'set_base_price': case 'set_customer_price': return params.value !== undefined && params.value !== '';
      case 'set_promo_price': return true;
      case 'discount_percent': return Number(params.percent) > 0 && Number(params.percent) < 100;
      case 'discount_amount': return Number(params.amount) > 0;
      case 'set_promo_validity': return Boolean(params.valid_from || params.valid_to);
      case 'link_promo': return Boolean(params.promo_code);
      case 'set_status': return Boolean(params.status);
      case 'set_vat': return params.value !== undefined;
      case 'set_visibility': return params.visible_to_agents !== undefined;
      case 'set_commercial_notes': return Boolean(params.text);
      case 'set_fido_bolletta': case 'set_tasso_zero': case 'set_voucher': return params.enabled !== undefined;
      case 'remove_promo': return true;
      default: return false;
    }
  }, [operation, params]);

  const renderParamsForm = () => {
    switch (operation) {
      case 'set_base_price':
      case 'set_promo_price':
      case 'set_customer_price':
        return (
          <div className="w-60">
            <Label className="text-xs">Nuovo valore (€) {operation === 'set_promo_price' && '— vuoto per rimuovere'}</Label>
            <Input type="number" value={params.value ?? ''} onChange={(e) => setParam('value')(e.target.value === '' ? null : Number(e.target.value))} data-testid="param-value" />
          </div>
        );
      case 'discount_percent':
        return (
          <div className="flex gap-4 items-end">
            <div className="w-40">
              <Label className="text-xs">Sconto %</Label>
              <Input type="number" value={params.percent ?? ''} onChange={(e) => setParam('percent')(Number(e.target.value))} data-testid="param-percent" />
            </div>
            <div className="w-52">
              <Label className="text-xs">Applica su (calcolato dal prezzo base)</Label>
              <Select value={params.target || 'promo_price'} onValueChange={setParam('target')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRICE_TARGETS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        );
      case 'discount_amount':
        return (
          <div className="flex gap-4 items-end">
            <div className="w-40">
              <Label className="text-xs">Sconto (€)</Label>
              <Input type="number" value={params.amount ?? ''} onChange={(e) => setParam('amount')(Number(e.target.value))} data-testid="param-amount" />
            </div>
            <div className="w-52">
              <Label className="text-xs">Applica su (calcolato dal prezzo base)</Label>
              <Select value={params.target || 'promo_price'} onValueChange={setParam('target')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRICE_TARGETS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        );
      case 'set_promo_validity':
        return (
          <div className="flex gap-4">
            <div><Label className="text-xs">Valida dal</Label><Input type="date" value={params.valid_from || ''} onChange={(e) => setParam('valid_from')(e.target.value)} /></div>
            <div><Label className="text-xs">Valida al</Label><Input type="date" value={params.valid_to || ''} onChange={(e) => setParam('valid_to')(e.target.value)} data-testid="param-valid-to" /></div>
          </div>
        );
      case 'link_promo':
        return (
          <div className="w-80">
            <Label className="text-xs">Promozione</Label>
            <Select value={params.promo_code || ''} onValueChange={setParam('promo_code')}>
              <SelectTrigger data-testid="param-promo"><SelectValue placeholder="Seleziona promo" /></SelectTrigger>
              <SelectContent>
                {(meta?.promotions || []).map((p) => <SelectItem key={p.code} value={p.code}>{p.code} — {p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        );
      case 'set_status':
        return (
          <div className="w-60">
            <Label className="text-xs">Nuovo stato</Label>
            <Select value={params.status || ''} onValueChange={setParam('status')}>
              <SelectTrigger data-testid="param-status"><SelectValue placeholder="Seleziona stato" /></SelectTrigger>
              <SelectContent>
                {(meta?.statuses || []).map((s) => <SelectItem key={s} value={s}>{PRODUCT_STATUS_CONFIG[s]?.label || s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        );
      case 'set_vat':
        return (
          <div className="w-40">
            <Label className="text-xs">IVA %</Label>
            <Select value={params.value !== undefined ? String(params.value) : ''} onValueChange={(v) => setParam('value')(Number(v))}>
              <SelectTrigger data-testid="param-vat"><SelectValue placeholder="IVA" /></SelectTrigger>
              <SelectContent>{[0, 4, 10, 22].map((v) => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}</SelectContent>
            </Select>
          </div>
        );
      case 'set_visibility':
        return (
          <div className="w-60">
            <Label className="text-xs">Visibile agli agenti</Label>
            <Select value={params.visible_to_agents !== undefined ? String(params.visible_to_agents) : ''} onValueChange={(v) => setParam('visible_to_agents')(v === 'true')}>
              <SelectTrigger data-testid="param-visibility"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">SI — visibile</SelectItem>
                <SelectItem value="false">NO — nascosto</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );
      case 'set_commercial_notes':
        return (
          <div className="space-y-3 w-full max-w-xl">
            <div>
              <Label className="text-xs">Testo note commerciali</Label>
              <Textarea rows={2} value={params.text || ''} onChange={(e) => setParam('text')(e.target.value)} data-testid="param-notes" />
            </div>
            <div className="w-60">
              <Label className="text-xs">Modalità</Label>
              <Select value={params.mode || 'replace'} onValueChange={setParam('mode')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="replace">Sostituisci</SelectItem>
                  <SelectItem value="append">Aggiungi in coda</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      case 'set_fido_bolletta':
      case 'set_tasso_zero':
        return (
          <div className="flex gap-4 items-end">
            <div className="w-44">
              <Label className="text-xs">Abilitato</Label>
              <Select value={params.enabled !== undefined ? String(params.enabled) : ''} onValueChange={(v) => setParam('enabled')(v === 'true')}>
                <SelectTrigger data-testid="param-enabled"><SelectValue placeholder="SI/NO" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">SI</SelectItem>
                  <SelectItem value="false">NO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {params.enabled && (
              <div className="w-40">
                <Label className="text-xs">Durata (mesi)</Label>
                <Input type="number" value={params.months ?? ''} placeholder={operation === 'set_tasso_zero' ? '24' : '48'} onChange={(e) => setParam('months')(e.target.value === '' ? null : Number(e.target.value))} />
              </div>
            )}
          </div>
        );
      case 'set_voucher':
        return (
          <div className="flex gap-4 items-end flex-wrap">
            <div className="w-44">
              <Label className="text-xs">Voucher</Label>
              <Select value={params.enabled !== undefined ? String(params.enabled) : ''} onValueChange={(v) => setParam('enabled')(v === 'true')}>
                <SelectTrigger data-testid="param-enabled"><SelectValue placeholder="SI/NO" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">SI</SelectItem>
                  <SelectItem value="false">NO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {params.enabled && (
              <>
                <div className="w-40">
                  <Label className="text-xs">Valore (€)</Label>
                  <Input type="number" value={params.amount ?? ''} onChange={(e) => setParam('amount')(e.target.value === '' ? null : Number(e.target.value))} />
                </div>
                <div className="flex-1 min-w-[250px]">
                  <Label className="text-xs">Condizioni</Label>
                  <Input value={params.conditions || ''} onChange={(e) => setParam('conditions')(e.target.value)} />
                </div>
              </>
            )}
          </div>
        );
      case 'remove_promo':
        return <p className="text-sm text-muted-foreground">Verranno rimossi: codice promo, date validità e prezzo promo dai prodotti selezionati.</p>;
      default:
        return <p className="text-sm text-muted-foreground">Seleziona un'operazione per configurare i parametri.</p>;
    }
  };

  const filterSelect = (label, key, options, renderLabel = (v) => v) => (
    <div className="w-[150px]">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={filters[key] || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, [key]: v === 'all' ? '' : v }))}>
        <SelectTrigger data-testid={`bulk-filter-${key}`}><SelectValue placeholder="Tutti" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutti</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{renderLabel(o)}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="p-6 max-w-[1700px] mx-auto" data-testid="bulk-edit-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/store/prodotti')} data-testid="back-to-products-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Zap className="h-7 w-7 text-amber-500" />
              Modifiche Rapide
            </h1>
            <p className="text-muted-foreground mt-1">Modifiche massive controllate: selezione → azione → anteprima → conferma</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <StepBadge number={1} label="Selezione" active={step === 1} done={step > 1} />
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <StepBadge number={2} label="Azione" active={step === 2} done={step > 2} />
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <StepBadge number={3} label="Anteprima e Conferma" active={step === 3} done={false} />
        </div>
      </div>

      {/* STEP 1: SELEZIONE */}
      {step === 1 && (
        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs text-muted-foreground">Ricerca</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Codice, marca, modello..." value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} className="pl-10" data-testid="bulk-search-input" />
                  </div>
                </div>
                {filterSelect('Categoria', 'category', meta?.categories || [], (c) => CATEGORY_LABELS[c] || c)}
                {filterSelect('Marca', 'brand', meta?.brands || [])}
                {filterSelect('Linea', 'product_line', meta?.product_lines || [])}
                {filterSelect('Stato', 'status', meta?.statuses || [], (s) => PRODUCT_STATUS_CONFIG[s]?.label || s)}
                {filterSelect('Promo', 'promo_code', (meta?.promotions || []).map((p) => p.code))}
                <Button variant="ghost" onClick={() => setFilters({ search: '', category: '', brand: '', product_line: '', status: '', promo_code: '' })}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <div>
                <CardTitle className="text-base">Prodotti ({products.length} filtrati)</CardTitle>
                <CardDescription>
                  <span className="font-semibold text-blue-600" data-testid="selected-count">{selectedIds.size}</span> prodotti selezionati
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={toggleAll} data-testid="select-all-btn">
                  {allVisible ? 'Deseleziona filtrati' : 'Seleziona tutti i filtrati'}
                </Button>
                {selectedIds.size > 0 && (
                  <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())}>Azzera selezione</Button>
                )}
              </div>
            </CardHeader>
            <div className="overflow-x-auto border-t">
              <table className="w-full text-sm" data-testid="bulk-products-table">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left text-xs uppercase text-muted-foreground">
                    <th className="px-3 py-2 w-10">
                      <Checkbox checked={allVisible} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
                    </th>
                    <th className="px-3 py-2">Codice</th>
                    <th className="px-3 py-2">Marca / Linea</th>
                    <th className="px-3 py-2">Modello</th>
                    <th className="px-3 py-2">Categoria</th>
                    <th className="px-3 py-2 text-right">Prezzo Base</th>
                    <th className="px-3 py-2 text-right">Prezzo Promo</th>
                    <th className="px-3 py-2">Promo</th>
                    <th className="px-3 py-2">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">Caricamento...</td></tr>
                  ) : products.map((p) => {
                    const statusCfg = PRODUCT_STATUS_CONFIG[p.effective_status || p.status] || {};
                    const checked = selectedIds.has(p.id);
                    return (
                      <tr key={p.id} className={`border-b cursor-pointer ${checked ? 'bg-blue-50' : 'hover:bg-slate-50'}`} onClick={() => toggleOne(p.id)} data-testid={`bulk-row-${p.product_code}`}>
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={checked} onCheckedChange={() => toggleOne(p.id)} data-testid={`bulk-check-${p.product_code}`} />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{p.product_code}</td>
                        <td className="px-3 py-2"><span className="font-medium">{p.brand}</span> <span className="text-muted-foreground text-xs">{p.product_line}</span></td>
                        <td className="px-3 py-2 max-w-[220px] truncate">{p.model}</td>
                        <td className="px-3 py-2"><Badge variant="secondary" className="text-xs">{CATEGORY_LABELS[p.category] || p.category}</Badge></td>
                        <td className="px-3 py-2 text-right">{formatPrice(p.base_price)}</td>
                        <td className="px-3 py-2 text-right text-blue-600">{p.promo_price ? formatPrice(p.promo_price) : '-'}</td>
                        <td className="px-3 py-2 text-xs">{p.promo_code || '-'}</td>
                        <td className="px-3 py-2"><Badge className={`text-xs border ${statusCfg.color || ''}`}>{statusCfg.label || p.status}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="flex justify-end">
            <Button size="lg" disabled={selectedIds.size === 0} onClick={() => setStep(2)} data-testid="goto-step2-btn">
              Continua con {selectedIds.size} prodotti
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* STEP 2: AZIONE */}
      {step === 2 && (
        <div className="space-y-4 max-w-3xl">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Scegli l'azione massiva</CardTitle>
              <CardDescription>{selectedIds.size} prodotti selezionati. Nessuna modifica verrà applicata senza anteprima e conferma.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="w-full max-w-md">
                <Label className="text-xs text-muted-foreground">Operazione</Label>
                <Select value={operation} onValueChange={(v) => { setOperation(v); setParams({}); }}>
                  <SelectTrigger data-testid="operation-select"><SelectValue placeholder="Seleziona operazione" /></SelectTrigger>
                  <SelectContent>
                    {['Prezzi', 'Promo', 'Stato', 'Note'].map((group) => (
                      <React.Fragment key={group}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">{group}</div>
                        {OPERATIONS.filter((o) => o.group === group).map((o) => (
                          <SelectItem key={o.type} value={o.type}>{o.label}</SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {operation && (
                <div className="border rounded-lg p-4 bg-slate-50">
                  {renderParamsForm()}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Torna alla selezione
            </Button>
            <Button size="lg" disabled={!operation || !paramsValid || working} onClick={handlePreview} data-testid="goto-preview-btn">
              {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
              Genera Anteprima
            </Button>
          </div>
        </div>
      )}

      {/* STEP 3: ANTEPRIMA E CONFERMA */}
      {step === 3 && preview && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="h-5 w-5 text-blue-600" />
                Anteprima: {preview.operation.label}
              </CardTitle>
              <CardDescription className="flex gap-4 flex-wrap">
                <span><strong>{preview.summary.products_selected}</strong> selezionati</span>
                <span className="text-blue-600"><strong>{preview.summary.products_affected}</strong> verranno modificati</span>
                <span><strong>{preview.summary.total_changes}</strong> modifiche totali</span>
                {preview.summary.total_warnings > 0 && (
                  <span className="text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4" /> <strong>{preview.summary.total_warnings}</strong> warning
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <div className="overflow-x-auto border-t">
              <table className="w-full text-sm" data-testid="preview-table">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left text-xs uppercase text-muted-foreground">
                    <th className="px-3 py-2">Codice</th>
                    <th className="px-3 py-2">Prodotto</th>
                    <th className="px-3 py-2">Campo</th>
                    <th className="px-3 py-2">Valore Attuale</th>
                    <th className="px-3 py-2">Nuovo Valore</th>
                    <th className="px-3 py-2 text-right">Differenza</th>
                    <th className="px-3 py-2">Warning</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((item) => (
                    item.changes.length === 0 ? (
                      <tr key={item.product_id} className="border-b opacity-50">
                        <td className="px-3 py-2 font-mono text-xs">{item.product_code}</td>
                        <td className="px-3 py-2">{item.brand} {item.model}</td>
                        <td className="px-3 py-2 text-muted-foreground" colSpan={4}>Nessuna modifica (già al valore richiesto)</td>
                        <td className="px-3 py-2 text-xs text-amber-600">{item.warnings.join('; ')}</td>
                      </tr>
                    ) : item.changes.map((change, ci) => (
                      <tr key={`${item.product_id}-${ci}`} className="border-b" data-testid={`preview-row-${item.product_code}-${change.field}`}>
                        <td className="px-3 py-2 font-mono text-xs">{ci === 0 ? item.product_code : ''}</td>
                        <td className="px-3 py-2">{ci === 0 ? `${item.brand} ${item.model}` : ''}</td>
                        <td className="px-3 py-2 text-xs font-medium">{change.label}</td>
                        <td className="px-3 py-2"><span className="text-red-600 bg-red-50 px-2 py-0.5 rounded text-xs">{String(change.old ?? '-')}</span></td>
                        <td className="px-3 py-2"><span className="text-green-700 bg-green-50 px-2 py-0.5 rounded text-xs font-medium">{String(change.new ?? '-')}</span></td>
                        <td className="px-3 py-2 text-right text-xs">
                          {ci === 0 && item.price_diff !== null && item.price_diff !== undefined ? (
                            <span className={item.price_diff < 0 ? 'text-green-600' : 'text-red-600'}>
                              {item.price_diff > 0 ? '+' : ''}{formatPrice(item.price_diff, 2)}
                            </span>
                          ) : ''}
                        </td>
                        <td className="px-3 py-2 text-xs text-amber-600">{ci === 0 ? item.warnings.join('; ') : ''}</td>
                      </tr>
                    ))
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={() => setStep(2)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Modifica azione
            </Button>
            <div className="flex items-center gap-3">
              <p className="text-sm text-muted-foreground">La modifica verrà tracciata nell'audit log</p>
              <Button
                size="lg"
                className="bg-green-600 hover:bg-green-700"
                disabled={working || preview.summary.products_affected === 0}
                onClick={handleApply}
                data-testid="confirm-apply-btn"
              >
                {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Conferma e Applica ({preview.summary.products_affected} prodotti)
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
